import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, TrendingUp, Clock, AlertTriangle, DollarSign,
  LogOut, RefreshCw, Send, Check, X, Edit3, ChevronDown, ChevronRight,
  PieChart as PieIcon, BarChart3, Download, ShoppingCart, FileText, Upload, Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pendiente: 'bg-gray-100 text-gray-700 border-gray-300',
  vencido: 'bg-red-100 text-red-700 border-red-300',
  en_proceso: 'bg-blue-100 text-blue-700 border-blue-300',
  pagado: 'bg-green-100 text-green-700 border-green-300',
};
const STATUS_LABELS = {
  pendiente: 'Pendiente',
  vencido: 'Vencido',
  en_proceso: 'En proceso',
  pagado: 'Pagado',
};

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FinanzasPagosPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [plans, setPlans] = useState([]);
  const [config, setConfig] = useState({ operation_pct: 30, employees_pct: 25, utility_pct: 45 });
  const [loading, setLoading] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem('token');
    if (!t) { navigate('/login'); return; }
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    fetchAll();
  }, [navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dash, plansList, cfg] = await Promise.all([
        axios.get(`${API}/finance/dashboard`),
        axios.get(`${API}/finance/payment-plans`),
        axios.get(`${API}/finance/allocation-config`),
      ]);
      setDashboard(dash.data);
      setPlans(plansList.data);
      setConfig(cfg.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando finanzas');
    }
    setLoading(false);
  };

  const handleBackfill = async () => {
    const choice = window.prompt(
      'Que contratos incluir en la sincronizacion?\n\n' +
      '1 = Solo firmados (firma cliente + firma Analytica) — predeterminado\n' +
      '2 = Firmados + Enviados (esperando firma del cliente)\n' +
      '3 = Todos incluyendo Borradores\n\n' +
      'Escribe 1, 2 o 3 (o Cancelar):',
      '2'
    );
    if (!choice) return;
    const params = { include_sent: false, include_drafts: false, purge_orphans: true };
    if (choice === '2') params.include_sent = true;
    if (choice === '3') { params.include_sent = true; params.include_drafts = true; }
    try {
      const res = await axios.post(`${API}/finance/payment-plans/backfill`, null, { params });
      const d = res.data;
      const parts = [];
      if (d.created) parts.push(`${d.created} creados`);
      if (d.activated) parts.push(`${d.activated} activados`);
      const totalDel = (d.deleted_orphans || 0) + (d.deleted_archived || 0);
      if (totalDel) parts.push(`${totalDel} eliminados`);
      if (d.skipped_no_phases) parts.push(`${d.skipped_no_phases} sin fases`);
      toast.success(
        `Sincronizacion completa (${d.checked} contratos) · ${parts.join(' · ') || 'sin cambios'}`,
        { duration: 6000 }
      );
      if ((d.deleted_details || []).length > 0) {
        console.log('Planes eliminados:', d.deleted_details);
      }
      fetchAll();
    } catch (e) { toast.error('Error en sincronizacion'); }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const exportPlansXlsx = async () => {
    try {
      const r = await axios.get(`${API}/finance/payment-plans/export/xlsx`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `plan_pagos_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al exportar'); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#2F3D8E]">Finanzas · Cobros</h1>
            <p className="text-xs text-gray-500">Plan de pagos · Asignacion 30/25/45 · Dashboard</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="reload-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualizar
            </Button>
            <Button size="sm" variant="outline" onClick={handleBackfill}
                    className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white"
                    data-testid="sync-btn">
              Sincronizar contratos
            </Button>
            <Button size="sm" variant="outline" onClick={exportPlansXlsx}
                    className="border-[#4C8C3B] text-[#4C8C3B] hover:bg-[#4C8C3B] hover:text-white"
                    data-testid="export-plans-xlsx-btn">
              <Download className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/compras')}
                    className="border-[#2F3D8E] text-[#2F3D8E] hover:bg-[#2F3D8E] hover:text-white"
                    data-testid="compras-link-btn">
              <ShoppingCart className="w-4 h-4 mr-2" /> Compras / Flujo
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/contratos')}>Contratos</Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/finanzas')}>Facturas</Button>
            <Button size="sm" variant="outline" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" /> Salir
            </Button>
          </div>
        </div>
        <div className="container mx-auto px-4 flex gap-1 border-t">
          {[
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'plans', label: 'Planes de Pago' },
            { id: 'config', label: 'Configuracion' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
                    data-testid={`tab-${t.id}`}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === t.id ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {activeTab === 'dashboard' && <DashboardView dashboard={dashboard} loading={loading} />}
        {activeTab === 'plans' && (
          <PlansView plans={plans} expandedPlan={expandedPlan} setExpandedPlan={setExpandedPlan}
                     onChange={fetchAll} />
        )}
        {activeTab === 'config' && (
          <ConfigView config={config} onSaved={(c) => { setConfig(c); fetchAll(); }} />
        )}
      </main>
    </div>
  );
};

// ============= Dashboard View =============
const DashboardView = ({ dashboard, loading }) => {
  const trendData = useMemo(() => {
    if (!dashboard) return [];
    let cum = 0;
    return (dashboard.monthly || []).map(m => { cum += m.received; return { month: m.month, recibido: cum }; });
  }, [dashboard]);

  if (loading || !dashboard) return <div className="text-center py-20 text-gray-500">Cargando...</div>;
  const s = dashboard.summary;
  const allocation = dashboard.allocation || {};
  const monthlyAlloc = dashboard.monthly_allocation || [];
  const clients = dashboard.clients || [];

  const pieData = [
    { name: 'Operacion', value: allocation.operation || 0, color: '#2F3D8E' },
    { name: 'Empleados', value: allocation.employees || 0, color: '#5DA5D8' },
    { name: 'Utilidad', value: allocation.utility || 0, color: '#4C8C3B' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<TrendingUp className="w-5 h-5 text-green-600" />} label="Recibido"
                 value={fmtMoney(s.received)} sub={`${s.counts.pagado} pagos completados`}
                 bg="bg-green-50" testid="kpi-received" />
        <KpiCard icon={<Clock className="w-5 h-5 text-amber-600" />} label="Pendiente"
                 value={fmtMoney(s.pending)}
                 sub={`${(s.counts.pendiente + s.counts.en_proceso + s.counts.vencido)} pagos pendientes`}
                 bg="bg-amber-50" testid="kpi-pending" />
        <KpiCard icon={<AlertTriangle className="w-5 h-5 text-red-600" />} label="Vencido"
                 value={fmtMoney(s.overdue)} sub={`${s.counts.vencido} pagos vencidos`}
                 bg="bg-red-50" testid="kpi-overdue" />
        <KpiCard icon={<DollarSign className="w-5 h-5 text-[#2F3D8E]" />} label="Total Contratado"
                 value={fmtMoney(s.total_contracted)} sub={`${s.plans_count} contratos activos`}
                 bg="bg-blue-50" testid="kpi-total" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <AllocationCard label="Operacion" value={allocation.operation} pct={allocation.config?.operation_pct} color="#2F3D8E" />
        <AllocationCard label="Empleados" value={allocation.employees} pct={allocation.config?.employees_pct} color="#5DA5D8" />
        <AllocationCard label="Utilidad" value={allocation.utility} pct={allocation.config?.utility_pct} color="#4C8C3B" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border lg:col-span-2">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Recibido por mes con asignacion
          </h3>
          {monthlyAlloc.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Aun no hay pagos registrados</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthlyAlloc}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Legend />
                <Bar dataKey="operation" stackId="a" fill="#2F3D8E" name="Operacion" />
                <Bar dataKey="employees" stackId="a" fill="#5DA5D8" name="Empleados" />
                <Bar dataKey="utility" stackId="a" fill="#4C8C3B" name="Utilidad" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl p-4 border">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <PieIcon className="w-4 h-4" /> Distribucion total
          </h3>
          {pieData.every(p => p.value === 0) ? (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Sin datos aun</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                     label={(d) => `${d.name}`}>
                  {pieData.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {trendData.length > 1 && (
        <div className="bg-white rounded-xl p-4 border">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Tendencia acumulada recibido
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Line type="monotone" dataKey="recibido" stroke="#4C8C3B" strokeWidth={3} dot={{ r: 5 }} name="Acumulado" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl p-4 border">
        <h3 className="font-semibold text-gray-700 mb-3">Por cliente</h3>
        {clients.length === 0 ? (
          <div className="text-center text-gray-400 py-6 text-sm">No hay clientes con planes de pago</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-gray-500">
                  <th className="py-2">Cliente</th>
                  <th className="py-2">Contratos</th>
                  <th className="py-2 text-right">Total</th>
                  <th className="py-2 text-right text-green-700">Recibido</th>
                  <th className="py-2 text-right text-amber-700">Pendiente</th>
                  <th className="py-2 text-right text-red-700">Vencido</th>
                  <th className="py-2 text-right">Pagos pend.</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50" data-testid="client-row">
                    <td className="py-2 font-medium">{c.client_company_name}</td>
                    <td className="py-2">{c.contracts.length}</td>
                    <td className="py-2 text-right">{fmtMoney(c.total_amount)}</td>
                    <td className="py-2 text-right text-green-700 font-semibold">{fmtMoney(c.received)}</td>
                    <td className="py-2 text-right text-amber-700">{fmtMoney(c.pending)}</td>
                    <td className="py-2 text-right text-red-700">{fmtMoney(c.overdue)}</td>
                    <td className="py-2 text-right">{c.pending_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const KpiCard = ({ icon, label, value, sub, bg, testid }) => (
  <div className={`${bg} rounded-xl p-4 border border-gray-200`} data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase text-gray-600 font-semibold">{icon}{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
    <div className="text-xs text-gray-500 mt-1">{sub}</div>
  </div>
);

const AllocationCard = ({ label, value, pct, color }) => (
  <div className="bg-white rounded-xl p-4 border" style={{ borderLeft: `4px solid ${color}` }}>
    <div className="text-xs uppercase text-gray-500 font-semibold">{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{fmtMoney(value)}</div>
    <div className="text-xs text-gray-400 mt-1">{pct ? `${pct}% del recibido` : ''}</div>
  </div>
);

// ============= Plans View =============
const PlansView = ({ plans, expandedPlan, setExpandedPlan, onChange }) => {
  if (!plans || plans.length === 0) {
    return (
      <div className="bg-white rounded-xl p-10 text-center border">
        <Wallet className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500">No hay planes de pago aun. Firma un contrato para que se generen automaticamente.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {plans.map(p => (
        <PlanRow key={p.id} plan={p} expanded={expandedPlan === p.id}
                 onToggle={() => setExpandedPlan(expandedPlan === p.id ? null : p.id)}
                 onChange={onChange} />
      ))}
    </div>
  );
};

const PlanRow = ({ plan, expanded, onToggle, onChange }) => {
  const items = plan.items || [];
  const received = items.filter(i => i.status === 'pagado').reduce((s, i) => s + (i.paid_amount || 0), 0);
  const pending = items.filter(i => i.status !== 'pagado').reduce((s, i) => s + (i.amount || 0), 0);
  const progress = plan.total_amount > 0 ? Math.min(100, (received / plan.total_amount) * 100) : 0;
  const applySurcharge = plan.apply_payphone_surcharge !== false; // default true
  const [savingSurcharge, setSavingSurcharge] = React.useState(false);

  const toggleSurcharge = async (e) => {
    e.stopPropagation();
    setSavingSurcharge(true);
    try {
      await axios.patch(`${API}/finance/payment-plans/${plan.id}`, { apply_payphone_surcharge: !applySurcharge });
      toast.success(!applySurcharge
        ? 'Recargo 5% activado · los links re-generados ya lo incluyen'
        : 'Recargo 5% desactivado · los links se actualizan automaticamente');
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSavingSurcharge(false); }
  };

  const deletePlan = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminar definitivamente el plan de pagos de "${plan.client_company_name}" (${plan.contract_number || 'sin numero'})?\n\nEsta accion no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/finance/payment-plans/${plan.id}`);
      toast.success('Plan de pagos eliminado');
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
  };

  return (
    <div className="bg-white rounded-xl border" data-testid="plan-row">
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50">
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <div>
            <div className="font-semibold text-gray-900">{plan.client_company_name}</div>
            <div className="text-xs text-gray-500">{plan.contract_number} · {plan.software_name || 'sin software'}</div>
          </div>
        </div>
        <div className="flex items-center gap-6 text-sm">
          {/* Toggle recargo Payphone 5% */}
          <div onClick={toggleSurcharge}
               className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition
                          ${applySurcharge ? 'border-purple-300 bg-purple-50 hover:bg-purple-100' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}
                          ${savingSurcharge ? 'opacity-60 pointer-events-none' : ''}`}
               title={applySurcharge ? 'Recargo 5% activo - click para desactivar' : 'Recargo 5% desactivado - click para activar'}
               data-testid={`toggle-surcharge-${plan.id}`}>
            <div className={`w-8 h-4 rounded-full transition-colors relative ${applySurcharge ? 'bg-purple-500' : 'bg-gray-300'}`}>
              <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all ${applySurcharge ? 'right-0.5' : 'left-0.5'}`} />
            </div>
            <span className={`text-[11px] font-medium ${applySurcharge ? 'text-purple-700' : 'text-gray-500'}`}>
              Recargo Payphone 5%
            </span>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Recibido</div>
            <div className="font-bold text-green-700">{fmtMoney(received)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Pendiente</div>
            <div className="font-bold text-amber-700">{fmtMoney(pending)}</div>
          </div>
          <div className="w-32 hidden md:block">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full" style={{ width: `${progress}%` }} />
            </div>
            <div className="text-xs text-gray-500 mt-1 text-right">{progress.toFixed(0)}%</div>
          </div>
          <button onClick={deletePlan}
                  className="text-red-400 hover:text-red-600 transition-colors p-1.5 rounded hover:bg-red-50"
                  title="Eliminar plan de pagos"
                  data-testid={`delete-plan-${plan.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </button>
      {expanded && (
        <div className="border-t px-4 py-3 space-y-2">
          {items.length === 0 ? (
            <div className="text-sm text-gray-400 py-3">Este contrato no tiene fases de pago definidas en el wizard.</div>
          ) : items.map(it => (
            <PaymentItemRow key={it.id} item={it} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
};

const PaymentItemRow = ({ item, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [form, setForm] = useState({
    status: item.status,
    due_date: item.due_date || '',
    paid_date: item.paid_date || '',
    paid_amount: item.paid_amount ?? item.amount,
    notes: item.notes || '',
    invoice_number: item.invoice_number || '',
    invoice_date: item.invoice_date || '',
  });
  const [saving, setSaving] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.paid_amount === '' || payload.paid_amount === null) delete payload.paid_amount;
      else payload.paid_amount = parseFloat(payload.paid_amount);
      if (!payload.invoice_number) delete payload.invoice_number;
      if (!payload.invoice_date) delete payload.invoice_date;
      await axios.patch(`${API}/finance/payment-items/${item.id}`, payload);
      toast.success('Cuota actualizada');
      setEditing(false);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error guardando'); }
    setSaving(false);
  };

  const genPayphoneLink = async () => {
    setSendingLink(true);
    try {
      const res = await axios.post(`${API}/finance/payment-items/${item.id}/payphone-link`);
      try {
        await navigator.clipboard.writeText(res.data.payphone_link);
        toast.success('Link Payphone copiado al portapapeles. Envialo al cliente.');
      } catch {
        toast.success(`Link generado: ${res.data.payphone_link}`);
      }
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error generando link'); }
    setSendingLink(false);
  };

  return (
    <div className="border rounded-lg p-3 bg-gray-50 flex flex-wrap items-center gap-3" data-testid="payment-item">
      <div className="flex-1 min-w-[200px]">
        <div className="font-medium text-sm">{item.description}</div>
        <div className="text-xs text-gray-500">Semana {item.week} · Vence: {item.due_date || '-'}</div>
        {item.notes && <div className="text-xs text-gray-400 italic mt-1">{item.notes}</div>}
      </div>
      <div className="text-right">
        <div className="text-xs text-gray-500">Monto</div>
        <div className="font-bold">{fmtMoney(item.amount)}</div>
        {item.status === 'pagado' && item.paid_date && (
          <div className="text-xs text-green-600">pagado {item.paid_date}</div>
        )}
      </div>
      <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_COLORS[item.status]}`}>
        {STATUS_LABELS[item.status]}
      </span>

      {!editing ? (
        <div className="flex gap-2">
          {item.status !== 'pagado' && (
            <Button size="sm" variant="outline" onClick={genPayphoneLink} disabled={sendingLink}
                    className="border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white"
                    data-testid="payphone-link-btn" title="Genera link de pago Payphone y lo copia al portapapeles">
              <Send className="w-4 h-4 mr-1" /> {sendingLink ? '...' : 'Payphone'}
            </Button>
          )}
          {item.invoice_pdf_url ? (
            <a href={item.invoice_pdf_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center text-xs px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50"
               data-testid={`invoice-pdf-link-${item.id}`}>
              <FileText className="w-4 h-4 mr-1" /> Factura
              {item.invoice_number && <span className="ml-1">#{item.invoice_number}</span>}
            </a>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => setShowInvoice(true)}
                  className="border-[#4C8C3B] text-[#4C8C3B] hover:bg-[#4C8C3B] hover:text-white"
                  data-testid={`upload-invoice-btn-${item.id}`}>
            <Upload className="w-4 h-4 mr-1" /> {item.invoice_pdf_url ? 'Cambiar factura' : 'Subir factura'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-item-btn">
            <Edit3 className="w-4 h-4 mr-1" /> Editar
          </Button>
        </div>
      ) : (
        <div className="w-full mt-2 grid grid-cols-1 md:grid-cols-6 gap-2">
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="border rounded px-2 py-1 text-sm" data-testid="status-select">
            <option value="pendiente">Pendiente</option>
            <option value="en_proceso">En proceso</option>
            <option value="pagado">Pagado</option>
            <option value="vencido">Vencido</option>
          </select>
          <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} placeholder="Vencimiento" />
          <Input type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} placeholder="Fecha pago" />
          <Input type="number" step="0.01" value={form.paid_amount}
                 onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
                 placeholder="Monto recibido" />
          <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                 placeholder="# Factura" data-testid={`invoice-number-input-${item.id}`} />
          <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                 placeholder="Fecha emision factura" data-testid={`invoice-date-input-${item.id}`} />
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                 placeholder="Observaciones" className="md:col-span-5" />
          <div className="md:col-span-6 flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}><X className="w-4 h-4 mr-1" /> Cancelar</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#4C8C3B] hover:bg-[#3d7030]" data-testid="save-item-btn">
              <Check className="w-4 h-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      )}
      {showInvoice && (
        <InvoiceUploadModal item={item}
                            onClose={() => setShowInvoice(false)}
                            onUploaded={() => { setShowInvoice(false); onChange(); }} />
      )}
    </div>
  );
};

// ============= Invoice Upload Modal =============
const InvoiceUploadModal = ({ item, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [invoiceNum, setInvoiceNum] = useState(item.invoice_number || '');
  const [invoiceDate, setInvoiceDate] = useState(item.invoice_date || '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { toast.error('Selecciona el PDF de la factura'); return; }
    if (file.type !== 'application/pdf') { toast.error('Solo PDF'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('PDF excede 10MB'); return; }
    setBusy(true);
    try {
      const data_url = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej;
        r.readAsDataURL(file);
      });
      await axios.post(`${API}/finance/payment-items/${item.id}/invoice`, {
        filename: file.name, data_url,
        invoice_number: invoiceNum || null, invoice_date: invoiceDate || null,
      });
      toast.success('Factura subida a Cloudinary');
      onUploaded?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error subiendo factura'); }
    setBusy(false);
  };

  const removeInvoice = async () => {
    if (!window.confirm('Eliminar la factura adjunta de esta cuota?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/finance/payment-items/${item.id}/invoice`);
      toast.success('Factura eliminada');
      onUploaded?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error eliminando'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         data-testid={`invoice-modal-${item.id}`}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-[#2F3D8E] to-[#4C8C3B] text-white rounded-t-xl">
          <h3 className="font-bold flex items-center gap-2"><FileText className="w-5 h-5" /> Factura de la cuota</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
            <div><strong>{item.description}</strong></div>
            <div>Monto: ${item.amount} · Vence: {item.due_date || '-'}</div>
          </div>
          <div>
            <label className="text-xs font-medium">PDF de la factura (max 10MB)</label>
            <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files[0])}
                   className="text-xs block mt-1" data-testid={`invoice-file-input-${item.id}`} />
            {file && <div className="text-[11px] text-gray-500 mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
          </div>
          <div>
            <label className="text-xs font-medium"># Factura</label>
            <Input value={invoiceNum} onChange={e => setInvoiceNum(e.target.value)}
                   placeholder="Ej: 001-001-000000123" data-testid={`invoice-num-modal-${item.id}`} />
          </div>
          <div>
            <label className="text-xs font-medium">Fecha de emision</label>
            <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
                   data-testid={`invoice-date-modal-${item.id}`} />
          </div>
          {item.invoice_pdf_url && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
              Ya existe una factura adjunta:{' '}
              <a href={item.invoice_pdf_url} target="_blank" rel="noreferrer" className="underline">
                {item.invoice_pdf_filename || 'ver PDF'}
              </a>. Subir un nuevo PDF la reemplaza.
            </div>
          )}
        </div>
        <div className="p-3 border-t bg-gray-50 flex justify-between rounded-b-xl">
          {item.invoice_pdf_url ? (
            <Button variant="outline" onClick={removeInvoice} disabled={busy}
                    className="border-red-400 text-red-600" data-testid={`invoice-remove-${item.id}`}>
              <X className="w-4 h-4 mr-1" /> Quitar
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
            <Button onClick={submit} disabled={busy || !file}
                    className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white"
                    data-testid={`invoice-submit-${item.id}`}>
              {busy ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Subir
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= Config View =============
const ConfigView = ({ config, onSaved }) => {
  const [form, setForm] = useState(config);
  useEffect(() => setForm(config), [config]);
  const total = (+form.operation_pct || 0) + (+form.employees_pct || 0) + (+form.utility_pct || 0);
  const valid = Math.abs(total - 100) < 0.5;
  const save = async () => {
    try {
      const res = await axios.put(`${API}/finance/allocation-config`, {
        operation_pct: parseFloat(form.operation_pct),
        employees_pct: parseFloat(form.employees_pct),
        utility_pct: parseFloat(form.utility_pct),
      });
      toast.success('Porcentajes actualizados');
      onSaved(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="max-w-xl bg-white rounded-xl p-6 border">
      <h2 className="text-lg font-bold text-[#2F3D8E] mb-1">Asignacion mensual de ingresos</h2>
      <p className="text-sm text-gray-500 mb-4">Estos porcentajes se aplican sobre el total mensual recibido para distribuir entre Operacion, Empleados y Utilidad.</p>
      <div className="space-y-4">
        {[
          { key: 'operation_pct', label: 'Operacion (%)' },
          { key: 'employees_pct', label: 'Pago empleados (%)' },
          { key: 'utility_pct', label: 'Utilidad (%)' },
        ].map(f => (
          <div key={f.key}>
            <label className="text-sm font-medium text-gray-700">{f.label}</label>
            <Input type="number" step="0.01" value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                   data-testid={`config-${f.key}`} />
          </div>
        ))}
        <div className={`p-3 rounded text-sm ${valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          Total: {total.toFixed(2)}% {valid ? ' ✓' : ' (debe sumar exactamente 100%)'}
        </div>
        <Button onClick={save} disabled={!valid} className="bg-[#2F3D8E] hover:bg-[#1f2a6e]" data-testid="save-config-btn">
          Guardar configuracion
        </Button>
      </div>
    </div>
  );
};

export default FinanzasPagosPage;
